import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { formatDate } from "#/components/utls/time";
import Typography from "#/components/shared/typography";
import { useMessageTemplatePreview } from "#/message-templates/hooks/useMessageTemplatePreview";
import { MessageTemplateListItem } from "#/services/messageTemplates";

const statusClasses: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-slate-100 text-slate-600",
  draft: "bg-amber-100 text-amber-700",
  archived: "bg-slate-100 text-slate-600",
};

type MessageTemplateRowProps = {
  item: MessageTemplateListItem;
  contextKey: string;
};

function formatStatus(status?: string | null) {
  if (!status) return "Unknown";
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function MessageTemplateRow({ item, contextKey }: MessageTemplateRowProps) {
  const navigate = useNavigate();
  const previewQuery = useMessageTemplatePreview(item.id, contextKey);

  const updatedLabel = useMemo(() => {
    const value = item.updated_at || item.created_at || "";
    return value ? formatDate(value) : "Unknown date";
  }, [item.created_at, item.updated_at]);

  const normalizedStatus =
    item.status?.toLowerCase() ??
    (typeof item.is_active === "boolean"
      ? item.is_active
        ? "active"
        : "inactive"
      : "unknown");
  const statusClass = statusClasses[normalizedStatus] ??
    "bg-beige-100 text-brown-700";
  const displayName = item.title ?? item.name ?? item.key ?? "Untitled";

  return (
    <tr className="align-top">
      <td className="px-4 py-3">
        <Typography variant="body1" className="text-brown-900">
          {displayName}
        </Typography>
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${statusClass}`}
        >
          {formatStatus(
            item.status ??
              (typeof item.is_active === "boolean"
                ? item.is_active
                  ? "active"
                  : "inactive"
                : undefined)
          )}
        </span>
      </td>
      <td className="px-4 py-3 text-brown-700">{updatedLabel}</td>
      <td className="px-4 py-3 text-brown-700">
        {previewQuery.isLoading ? (
          <span className="text-xs text-brown-500">Loading…</span>
        ) : previewQuery.isError ? (
          <div className="flex flex-col gap-2">
            <span className="text-xs text-terracotta">
              Preview unavailable.
            </span>
            <div>
              <ActionButton
                size="sm"
                tone="secondary"
                onClick={() => previewQuery.refetch()}
              >
                Retry
              </ActionButton>
            </div>
          </div>
        ) : previewQuery.data ? (
          <span>{previewQuery.data}</span>
        ) : (
          <span className="text-xs text-brown-500">No preview available.</span>
        )}
      </td>
      <td className="px-4 py-3">
        <ActionButton
          size="sm"
          tone="secondary"
          onClick={() => navigate(`/admin/templates/${item.id}/edit`)}
        >
          Edit
        </ActionButton>
      </td>
    </tr>
  );
}

export default MessageTemplateRow;
